import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import ProtectedContent from 'app/frontend/base/components/ProtectedContent'
import Section from 'app/frontend/base/components/Section'
import { useTeam } from 'app/frontend/base/config/context/team'
import { Calendar, Columns3, List } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-use'
import EventList from './components/EventList'
import EventOverview from './components/EventOverview'
import EventCalendar from './components/EventCalendar'
import AddEventDialog from './components/AddEventDialog'
import LinkButton from '@/components/ui/link-button'

const TeamEventOverview = () => {
  const { team, guild } = useTeam()
  const { t } = useTranslation()

  const { hash: initialView } = useLocation()
  const [addingEvent, setAddingEvent] = useState(false)
  const [activeView, setActiveView] = useState(
    initialView ? initialView.replace('#', '') : 'overview',
  )

  const handleViewChange = (view: string) => {
    setActiveView(view)
    history.replaceState({}, '', `#${view}`)
  }

  return (
    <Section title={`Event Overview | ${team.name} | ${guild.name}`}>
      <Tabs
        defaultValue='overview'
        value={activeView}
        onValueChange={handleViewChange}
      >
        <h2 className='flex justify-between items-center'>
          <span className='flex items-center gap-3'>
            <span>{t('shared.events')}</span>
          </span>

          <div className='flex items-center gap-3'>
            <TabsList className='h-9'>
              <TabsTrigger value='overview'>
                <Tooltip delayDuration={500}>
                  <TooltipTrigger asChild>
                    <Columns3 size={16} />
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('dashboard.team.events.overview-view')}
                  </TooltipContent>
                </Tooltip>
              </TabsTrigger>

              <TabsTrigger value='list'>
                <Tooltip delayDuration={500}>
                  <TooltipTrigger asChild>
                    <List size={16} />
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('dashboard.team.roster.list-view')}
                  </TooltipContent>
                </Tooltip>
              </TabsTrigger>

              <TabsTrigger value='calendar'>
                <Tooltip delayDuration={500}>
                  <TooltipTrigger asChild>
                    <Calendar size={16} />
                  </TooltipTrigger>
                  <TooltipContent>
                    {t('dashboard.team.events.calendar-view')}
                  </TooltipContent>
                </Tooltip>
              </TabsTrigger>
            </TabsList>

            <ProtectedContent
              section='events'
              requirement='admin'
              renderUnauthorized={false}
            >
              <Button variant='outline' onClick={() => setAddingEvent(true)}>
                {t('dashboard.team.events.add-event')}
              </Button>
            </ProtectedContent>

            <ProtectedContent
              section='base'
              requirement='admin'
              renderUnauthorized={false}
            >
              <LinkButton to={`${team.path}/settings#events`}>
                {t('dashboard.team.events.edit-schedule')}
              </LinkButton>
            </ProtectedContent>
          </div>
        </h2>

        <AddEventDialog open={addingEvent} setOpen={setAddingEvent} />

        <TabsContent value={'list'} id={'list'}>
          <EventList />
        </TabsContent>

        <TabsContent value={'overview'} id={'overview'}>
          <EventOverview />
        </TabsContent>

        <TabsContent value={'calendar'} id={'calendar'}>
          <EventCalendar />
        </TabsContent>
      </Tabs>
    </Section>
  )
}

export default TeamEventOverview
